import React, { useState } from 'react'
import { FieldArray, FieldArrayRenderProps, FormikProps } from 'formik'
import { AsyncView } from '@aboutbits/react-toolbox'
import { FormattedMessage, useIntl } from 'react-intl'
import { useGetArticlesByIds } from '../db/article'
import { populateArticleData } from '../detail/populateArticleData'
import {
  Section,
  SectionItem,
  SectionList,
  SectionTitle,
} from '../../../shared/section'
import { SectionListAddButton } from '../../../shared/section/SectionListAddButton'
import { ContentArea } from '../../../shared/content'
import { SectionListFormError } from '../../../shared/section/SectionListFormError'
import { translationsActivity } from '../translations/translationsActivity'
import { ArticleFormItem } from './activityForm/ArticleFormItem'
import { ActivityFormFields } from './types'
import { ArticleDialog } from './activityForm/ArticleDialog'
import { ArticleEditDialog } from './activityForm/ArticleEditDialog'

const ArticleFormSection: React.FC<{
  formik: FormikProps<ActivityFormFields>
  selectableIds: string[]
}> = ({ formik, selectableIds }) => {
  const intl = useIntl()
  const { values, setFieldValue } = formik
  const [showAddDialog, setShowAddDialog] = useState<boolean>(false)
  const [amountDialog, setAmountDialog] = useState<{
    show: boolean
    index: number
    count: string
    description: string
    articleId: string
  }>({ show: false, index: 0, count: '1', description: '', articleId: '' })
  const [indexOfSelectedRecord, setIndexOfSelectedRecord] = useState<
    number | null
  >(null)
  let arrayHelperBinding: FieldArrayRenderProps

  const toggleSelectedRecord = (id: number) => {
    if (indexOfSelectedRecord === id) {
      setIndexOfSelectedRecord(null)
    } else {
      setIndexOfSelectedRecord(id)
    }
  }

  const updateActivityAmount = (amount: string, descriptionText: string) => {
    const index = amountDialog.index
    const article = values.articles[index]

    setFieldValue(`articles[${index}]`, {
      ...article,
      count: amount,
      description: descriptionText,
    })

    setAmountDialog({
      show: false,
      index: 0,
      count: '1',
      description: '',
      articleId: '',
    })
  }

  const { data } = useGetArticlesByIds(
    values.articles.map((article) => article.articleId)
  )
  const articleDetails = populateArticleData(values.articles, data)

  return (
    <ContentArea>
      <div className="mb-10 space-y-5">
        <Section>
          <SectionTitle>
            <FormattedMessage {...translationsActivity.detailArticle} />
          </SectionTitle>
          <AsyncView
            data={articleDetails}
            renderSuccess={(articleDetails) => (
              <SectionList>
                <FieldArray
                  name="articles"
                  render={(helper) => {
                    arrayHelperBinding = helper

                    return (
                      <>
                        {articleDetails && articleDetails.length > 0 ? (
                          articleDetails.map((article, index) => (
                            <ArticleFormItem
                              key={index}
                              article={article}
                              showActions={indexOfSelectedRecord === index}
                              onClick={() => toggleSelectedRecord(index)}
                              onEditClick={() => {
                                setAmountDialog({
                                  count: values.articles[index].count,
                                  show: true,
                                  index,
                                  description:
                                    values.articles[index].description,
                                  articleId: article.id,
                                })
                              }}
                              onDeleteClick={() => {
                                helper.remove(index)
                                toggleSelectedRecord(index)
                              }}
                            />
                          ))
                        ) : (
                          <SectionItem>
                            <FormattedMessage
                              id="activity.form.articles.empty"
                              defaultMessage="No articels were needed"
                            />
                          </SectionItem>
                        )}
                      </>
                    )
                  }}
                />
                <SectionListFormError name="articles" />
              </SectionList>
            )}
          />
        </Section>
        <SectionListAddButton
          text={intl.formatMessage(translationsActivity.articleAdd)}
          onClick={() => {
            setShowAddDialog(true)
          }}
        />
      </div>
      <ArticleDialog
        onDismiss={() => {
          setShowAddDialog(false)
        }}
        isOpen={showAddDialog}
        onConfirm={(article) => {
          arrayHelperBinding.push(article)
        }}
        label={intl.formatMessage({
          id: 'activity.article.overview',
          defaultMessage: 'Article overview',
        })}
        selectableIds={selectableIds}
      />
      <ArticleEditDialog
        onDismiss={() => {
          setAmountDialog({
            show: false,
            count: '0',
            index: 0,
            description: '',
            articleId: '',
          })
        }}
        isOpen={amountDialog.show}
        onConfirm={updateActivityAmount}
        label={intl.formatMessage(translationsActivity.articleAmountTitle)}
        amount={amountDialog.count}
        descriptionText={amountDialog.description}
        articleId={amountDialog.articleId}
      />
    </ContentArea>
  )
}

export { ArticleFormSection }
